import { Alignment, ArrayUtil, Collection, DOMUtil, getLogger, getThemeForKey, HorizontalAlignment, ModelRow, StringUtil, VerticalAlignment } from "@mcleod/core";
import { ImageName } from "@mcleod/images";
import { Button, Component, Image, Label, SelectionListener, serializeComponents, Tabset } from "../..";
import { ComponentPropDefinition, ComponentPropDefinitions } from "../../base/ComponentProps";
import { ComponentTypes } from "../../base/ComponentTypes";
import { Cursor } from "../../base/Cursor";
import { ListenerListDef } from "../../base/ListenerListDef";
import { Event } from "../../events/Event";
import { TabCloseEvent, TabCloseListener } from "../../events/TabCloseEvent";
import { ComponentCreationCallback, deserializeComponents } from "../../serializer/ComponentDeserializer";
import { ButtonVariant } from "../button/ButtonVariant";
import { Panel } from "../panel/Panel";
import { TabPropDefinitions, TabProps } from "./TabProps";
import { TabStyle } from "./TabsetProps";
import { AllowedToSearch } from "../../databinding/AllowedToSearch";

const log = getLogger("components.tabset.Tab");

const _autoTitleLabelPrefix = "autoTitleLabel_";
const _beforeSelectionListenerDef: ListenerListDef = { listName: "_beforeSelectionListeners" };
const _afterSelectionListenerDef: ListenerListDef = { listName: "_afterSelectionListeners" };
const _beforeCloseListenerDef: ListenerListDef = { listName: "_beforeCloseListeners" };
const _afterCloseListenerDef: ListenerListDef = { listName: "_afterCloseListeners" };

export class Tab extends Panel implements TabProps {
  private _caption: string;
  private _heading: Panel;
  private _headingLabel: Label;
  private _titleLabel: Label;
  private _addlTitleCompsPanel: Panel;
  private _addlTitleComps: Component[];
  private _imageName: ImageName;
  private _closeable: boolean;
  private _closeButton: Button;
  private _headingImage: Image;
  _index: number;
  private _tabStyle: TabStyle;
  private _tabAlignment: Alignment;
  private _expanded: boolean;
  private buttonExpand: Button;
  private _tabset: Tabset;
  private _selected: boolean;
  private _showTitleInScroll: boolean = true;

  constructor(props?: Partial<TabProps>) {
    super({ fillRow: true });
    this.setProps(props);
  }

  override setProps(props: Partial<TabProps>) {
    super.setProps(props);
  }

  get caption(): string {
    return this._caption;
  }

  set caption(value: string) {
    const oldValue = this._caption;
    this._caption = value;
    if (this._headingLabel != null)
      this._headingLabel.caption = value;
    if (this._titleLabel != null)
      this._titleLabel.caption = value;
    this._matchIdToValue(oldValue, value);
  }

  get tabset(): Tabset {
    return this._tabset
  }

  set tabset(value: Tabset) {
    this._tabset = value;
    this.createTitle();
  }

  get closeable() {
    return this._closeable;
  }

  set closeable(value) {
    if (this._closeable === value)
      return;
    this._closeable = value;
    this._syncCloseable();
  }

  private _syncCloseable() {
    if (this._closeable === true) {
      this._closeButton = new Button({
        imageName: "x",
        variant: ButtonVariant.round,
        focusable: false,
        marginTop: 2,
        imageWidth: 14,
        imageHeight: 14,
        padding: 0,
        margin: 0,
        marginLeft: 6
      });
      this._closeButton.addClickListener((event) => {
        if (this._designer == null)
          this.close(event);
        else
          this._designer["closeTab"](this);
      });
      this._heading?.add(this._closeButton);
    }
    else {
      this._heading?.remove(this._closeButton);
      this._closeButton = undefined;
    }
  }

  select() {
    this._handleTabGrowing();
    this._tabset.selectedIndex = this._index;
    this._selected = true;
  }

  public selectAndScroll() {
    this.select();
    if (this.tabStyle === TabStyle.SCROLL)
      this.scrollTo();
  }

  private scrollTo() {
    AllowedToSearch.set(this);
    this._tabset.scrollToTab(this);
  }

  private _handleTabGrowing()
  {
    const tabGrew = this._tabset.growLastTab(this);
    if (tabGrew === true)
      this.growChildComponent(this._tabset.getContentHeight());
  }

  close(originatingEvent?: Event) {
    const beforeCloseEvent = this.fireListeners<TabCloseEvent>(_beforeCloseListenerDef, () => new TabCloseEvent(this, originatingEvent?.domEvent));
    if (beforeCloseEvent != null && beforeCloseEvent.afterEventPromise != null)
      beforeCloseEvent.afterEventPromise.then(event => this._internalClose(event, originatingEvent));
    else
      this._internalClose(beforeCloseEvent, originatingEvent);
  }

  private _internalClose(beforeCloseEvent: TabCloseEvent, originatingEvent?: Event) {
    if (beforeCloseEvent != null && beforeCloseEvent.defaultPrevented)
      return;
    this.parent.remove(this);
    this.fireListeners(_afterCloseListenerDef, () => new TabCloseEvent(this, originatingEvent?.domEvent));
  }

  get heading(): Panel {
    return this._heading;
  }

  get headingLabel(): Label {
    return this._headingLabel;
  }

  get showTitleInScroll(): boolean {
    return this._showTitleInScroll;
  }

  set showTitleInScroll(value: boolean) {
    this._showTitleInScroll = value;
    this.createTitle();
  }

  get tabStyle(): TabStyle {
    return this._tabStyle || TabStyle.UNDERLINED;
  }

  set tabStyle(value: TabStyle) {
    this._tabStyle = value;
    this._syncTabStyle();
  }

  get tabAlignment() {
    return this._tabAlignment  || Alignment.TOP;
  }

  set tabAlignment(value: Alignment) {
    this._tabAlignment = value;
  }

  _syncTabStyle() {
    const isAccordion = this.tabStyle === TabStyle.ACCORDION;
    const isOutlined = this.tabStyle === TabStyle.OUTLINED;
    const isUnderlined = this.tabStyle === TabStyle.UNDERLINED || this.tabStyle === TabStyle.SCROLL;
    const vert = this.isVertical();
    this.heading?.setProps({
      align: isAccordion ? HorizontalAlignment.LEFT : HorizontalAlignment.CENTER,
      borderWidth: isUnderlined ? 0 : 1,
      fillRow: isAccordion,
      rowBreak: isAccordion || vert,
      themeKey: isOutlined ? "tabset.unselectedTab" : null
    });
    this.headingLabel?.setProps({
      paddingTop: vert ? 0 : 4,
      paddingBottom: vert ? 0 : 4,
    });
    const tabAlign = this.tabsetTabAlignment;
    if (this.heading != null) {
      if (isOutlined) {
        this.heading.borderBottomWidth = tabAlign === Alignment.TOP ? 0 : 1;
        this.heading.borderRightWidth = this.tabset?.tabAlignment === Alignment.LEFT ? 0 : 1;
        this.heading.borderTopWidth = tabAlign === Alignment.BOTTOM ? 0 : 1;
        this.heading.borderLeftWidth = this.tabset?.tabAlignment === Alignment.RIGHT ? 0 : 1;
      }
      if (isAccordion)
        this.heading.color = "subtle.darker";
      if (tabAlign == Alignment.LEFT || tabAlign == Alignment.RIGHT) {
        this._headingImage.height = 38;
        this._headingImage.marginRight = null;
        this.heading.borderTopWidth = 0;
        this.heading.borderBottomWidth = 0;
        this.heading.borderWidth = 0;
        this.heading.borderRadius = 0;
        this.heading.borderTopColor = "subtle.light";
        this.heading.borderBottomColor = "subtle.light";
      }
    }
    this.rowBreak = true;
    this._syncExpandButton();
  }

  _syncExpandButton() {
    if (this.tabStyle === TabStyle.ACCORDION) {
      if (this.buttonExpand == null) {
        this.buttonExpand = new Button({ imageName: "chevron", variant: ButtonVariant.round, color: "subtle.darker", rowBreak: false });
        this.buttonExpand.addClickListener(() => this._toggleExpanded());
        this.buttonExpand.style.transitionDuration = "500ms";
      }
      if (this.heading != null && !this.heading?.contains(this.buttonExpand))
        this.heading.add(this.buttonExpand);
      if (this.expanded)
        this.buttonExpand.imageRotation = 0;
      else
        this.buttonExpand.imageRotation = 90;
    }
    else if (this.buttonExpand != null && this.heading?.contains(this.buttonExpand)) {
      this.heading.remove(this.buttonExpand);
      this.buttonExpand = null;
    }
  }

  createHeading() {
    switch (this.tabStyle) {
      case TabStyle.SCROLL:
        this._createScrollHeading();
        break;
      case TabStyle.ACCORDION:
        this._createAccordionHeading();
        break;
      default:
        this._createOutlinedUnderlinedHeading();
    }
    this.heading.rowBreak = true;
    this._heading?.setDesigner(this._designer);
    if (this._headingImage != null)
      this._heading?.insert(this._headingImage, this._heading.indexOf(this._headingLabel));
    this._syncCloseable();
    this._syncTabStyle();
  }

  private get tabsetTabAlignment(): Alignment {
    return this._tabset?.tabAlignment;
  }

  private isVertical() {
    return this._tabset?.isVertical();
  }

  private _createScrollHeading() {
    this._heading = new Panel({
      ...this._getSharedHeadingProps(),
      paddingLeft: 12,
      paddingRight: 12,
      marginLeft: 0,
      marginRight: 24,
      marginTop: 8,
      marginBottom: 8
    });
    this._headingLabel = this._createHeadingLabel();
    this._heading.add(this._headingLabel);
    this._heading.addClickListener(() => {
      this.select();
      this.scrollTo();
            this.selectInDesigner();
    });
  }

  private _createAccordionHeading() {
    this._heading = new Panel({
      ...this._getSharedHeadingProps(),
      paddingLeft: 18,
      paddingRight: 18,
      marginRight: 0
    });
    this._headingLabel = this._createHeadingLabel();
    this._heading.add(this._headingLabel);
    this._heading.addClickListener(() => this._toggleExpanded());
  }

  private _createOutlinedUnderlinedHeading() {
    const tabHeadingProps = { borderRadius: 0, borderColor: "strokeSecondary", borderRightWidth: 0 }
    const vert = this.isVertical();
    this._heading = new Panel({
      ...this._getSharedHeadingProps(),
      paddingLeft: vert ? 16 : 18,
      paddingRight: vert ? 12 : 18,
      paddingTop: vert ? 8: 4,
      paddingBottom: vert ? 8: 4,
      marginRight: this.tabsetTabAlignment === Alignment.TOP ? 6 : 0
    });
    this._headingLabel = this._createHeadingLabel();
    this._heading.add(this._headingLabel);
        this._heading.addClickListener(() => {
            this.select();
            this.selectInDesigner();
        });
  }

  private _getSharedHeadingProps() {
    const vert = this.isVertical();
    const align = this.tabsetTabAlignment;
    return {
      id: "tabHeading",
      rowBreak: false,
      cursor: Cursor.POINTER,
      align: HorizontalAlignment.CENTER,
      verticalAlign: VerticalAlignment.CENTER,
      color: "McLeodPrimary",
      minWidth:  vert ? null : 160,
      borderTopRightRadius: align === Alignment.TOP || align === Alignment.RIGHT ? 4: null,
      borderBottomRightRadius: align === Alignment.BOTTOM || align === Alignment.RIGHT ? 4 : null,
      borderTopLeftRadius: align === Alignment.TOP || align === Alignment.LEFT ? 4 : null,
      borderBottomLeftRadius: align === Alignment.BOTTOM || align === Alignment.LEFT ? 4 : null,
    }
  }

  private _createHeadingLabel(): Label {
    return new Label({
      caption: this.caption,
      rowBreak: false,
      fillRow: true,
      paddingRight: 0,
      paddingLeft: 0,
      allowSelect: false,
      collapseVerticallyWhenEmpty: true
    });
  }

  createTitle() {
    this.remove(this._titleLabel);
    if (this._showTitleInScroll !== true || this._tabset.usingScrollStyle() !== true)
      return;
    this._titleLabel = new Label({
      themeKey: "label.sectionHeading",
      id: _autoTitleLabelPrefix + this.id,
      caption: this.caption,
      rowBreak: false,
    });
    this.insert(this._titleLabel, 0);

    //if the layout didn't contain the addl title panel (that can accommodate additional components at the
    //same level as the title), add it (mainly so that users can add components in the designer).
    //remember, this only works when using the 'scroll' tab style and when we are showing the dynamically added title
    this.remove(this._addlTitleCompsPanel);
    if (ArrayUtil.isEmptyArray(this._addlTitleComps) !== true || this._designer != null) {
      this._addlTitleCompsPanel = new Panel({
        id: "panelAddlTitleComps",
        align: HorizontalAlignment.LEFT,
        verticalAlign: VerticalAlignment.CENTER,
        fillRow: true,
        rowBreak: true,
        margin: 0,
        padding: 0,
      });
      this._addlTitleCompsPanel.setDesigner(this._designer);
      if (this._addlTitleComps != null) {
        for (const addlTitleComp of this._addlTitleComps)
          this._addlTitleCompsPanel.add(addlTitleComp);
      }
      this.insert(this._addlTitleCompsPanel, 1);
    }
    else {
      this._titleLabel.fillRow = true;
      this._titleLabel.rowBreak = true;
    }
  }

  get expanded(): boolean {
    return this._expanded;
  }

  set expanded(value: boolean) {
    this.expandCollapseTab(value, true);
  }

  expandCollapseTab(value: boolean, animate: boolean) {
    this._expanded = value;
    if (this.tabStyle !== TabStyle.SCROLL) {
      const tabsetContent = this.heading.parent as Panel;
      //not showing the animation is necessary/useful when we don't want a delay in the action being performed
      //this is needed when we are initially setting up the tabset/tab...if we use the animation the delay can mean
      //that tabs are incorrectly expanded/collapsed because we may come through this method > 1 time as the
      //default accordion mode is defaulted/changed
      if (this.expanded) {
        tabsetContent.insert(this, tabsetContent.indexOf(this.heading) + 1);
        if (animate === true)
          this.expand();
        else
          this.expandNow();
                this.selectInDesigner();
      }
      else {
        if (animate === true)
          this.collapse().then(() => tabsetContent.remove(this));
        else {
          this.collapseNow();
          tabsetContent.remove(this);
        }
      }
      this._syncExpandButton();
    }
  }

  private _toggleExpanded() {
    this.expanded = !this.expanded;
  }

  _imageUpdated() {
    const vert = this.isVertical();
    if (this._headingImage == null && this._imageName != null) {
      this._headingImage = new Image({ rowBreak: vert, marginRight: vert ? 0 : 6, name: this.imageName });
      this._heading?.insert(this._headingImage, this._heading.indexOf(this._headingLabel));
      this.tabset?.refreshUnderline();
    } else if (this._headingImage != null && this._imageName == null) {
      this._heading?.remove(this._headingImage);
      this.tabset?.refreshUnderline();
    }
    else if (this._headingImage != null) {
      this._headingImage.name = this._imageName;
      this.tabset?.refreshUnderline();
    }
  }

  get imageName(): ImageName {
    return this._imageName;
  }

  set imageName(value: ImageName) {
    this._imageName = value;
    this._imageUpdated();
  }

  addBeforeSelectionListener(listener: SelectionListener) {
    this.addEventListener(_beforeSelectionListenerDef, listener);
  }

  removeBeforeSelectionListener(listener: SelectionListener) {
    this.removeEventListener(_beforeSelectionListenerDef, listener);
  }

  addAfterSelectionListener(listener: SelectionListener) {
    this.addEventListener(_afterSelectionListenerDef, listener);
  }

  removeAfterSelectionListener(listener: SelectionListener) {
    this.removeEventListener(_afterSelectionListenerDef, listener);
  }

  addBeforeCloseListener(listener: TabCloseListener) {
    this.addEventListener(_beforeCloseListenerDef, listener);
  }

  removeBeforeCloseListener(listener: TabCloseListener) {
    this.removeEventListener(_beforeCloseListenerDef, listener);
  }

  addAfterCloseListener(listener: TabCloseListener) {
    this.addEventListener(_afterCloseListenerDef, listener);
  }

  removeAfterCloseListener(listener: TabCloseListener) {
    this.removeEventListener(_afterCloseListenerDef, listener);
  }

  override displayData(data: ModelRow, allData: ModelRow[], rowIndex: number) {
  }

  set selected(value: boolean) {
    this._selected = value;
    this.heading.fontBold = value;
    this.heading.color = value ? "primary" : "subtle.darker";

    if (this.tabset?.tabStyle === TabStyle.OUTLINED)
      this.heading.applyTheme(getThemeForKey(value ? "tabset.selectedTab" : "tabset.unselectedTab"), true);
    if (this.tabAlignment === Alignment.LEFT || this.tabAlignment === Alignment.RIGHT) {
      this.heading.borderTopWidth = value ? 1 : 0;
      this.heading.borderBottomWidth = value ? 1 : 0;
    }
  }

  get selected(): boolean {
    return this._selected;
  }

  override getPropertyDefinitions(): ComponentPropDefinitions {
    return TabPropDefinitions.getDefinitions();
  }

  public override setDesigner(designer: any) {
    super.setDesigner(designer);
    this._heading?.setDesigner(this._designer);
  }

  override get serializationName() {
    return "tab";
  }

  override growChildComponent(height: number)
  {
    let availableHeight = height;
    availableHeight -= DOMUtil.getStyleAttrAsNumber(DOMUtil.getComputedStyle("paddingTop", this._element));
    availableHeight -= DOMUtil.getStyleAttrAsNumber(DOMUtil.getComputedStyle("paddingBottom", this._element));
    availableHeight -= DOMUtil.getStyleAttrAsNumber(DOMUtil.getComputedStyle("marginTop", this._element));
    availableHeight -= DOMUtil.getStyleAttrAsNumber(DOMUtil.getComputedStyle("marginBottom", this._element));
    let compToGrow: Component;
    for (const childComp of this._components) {
      if (childComp instanceof Label && childComp.id.startsWith("autoTitleLabel_")) {
        availableHeight -= DOMUtil.getStyleAttrAsNumber(DOMUtil.getComputedStyle("paddingTop", childComp._element));
        availableHeight -= DOMUtil.getStyleAttrAsNumber(DOMUtil.getComputedStyle("paddingBottom", childComp._element));
        availableHeight -= DOMUtil.getStyleAttrAsNumber(DOMUtil.getComputedStyle("marginTop", childComp._element));
        availableHeight -= DOMUtil.getStyleAttrAsNumber(DOMUtil.getComputedStyle("marginBottom", childComp._element));
        continue;
      }
      if (compToGrow != null)
        return;
      compToGrow = childComp;
    }
    if (compToGrow != null)
      compToGrow.growToContainerHeight(availableHeight);
  }

  /**
   * Don't serialize the auto-generated title label
   */
  _serializeNonProps(): string {
    if (this.components == null)
      return "";
    const compsToSerialize = [];
    const addlTitleComps = [];
    for (const comp of this._components) {
      if (comp instanceof Label && comp.id.startsWith(_autoTitleLabelPrefix))
        continue;
      if (comp instanceof Panel && comp.id === "panelAddlTitleComps") {
        for (const c of comp.components)
          addlTitleComps.push(c);
        continue;
      }
      compsToSerialize.push(comp);
    }
    if (compsToSerialize.length === 0)
      return "";
    let result = "\"components\": " + serializeComponents(compsToSerialize, null,) + ",\n";
    if (ArrayUtil.isEmptyArray(addlTitleComps) !== true) {
      result += "\"addlTitleComps\": " + serializeComponents(addlTitleComps, null,) + ",\n";
    }
    return result;
  }

  _deserializeSpecialProps(componentOwner, compDef, defaultPropValues, dataSources, componentCreationCallback: ComponentCreationCallback) {
    const result = super._deserializeSpecialProps(componentOwner, compDef, defaultPropValues, dataSources, componentCreationCallback);
    if (Array.isArray(compDef.addlTitleComps) && ArrayUtil.isEmptyArray(compDef.addlTitleComps) !== true) {
      this._addlTitleComps = deserializeComponents(componentOwner, compDef.addlTitleComps, this._designer, defaultPropValues, dataSources, componentCreationCallback);
    }
    return result;
  }

  /**
   * After deserializing, if the component list somehow included the auto-generated title label, set the member variable to reference that component.
   * This shouldn't happen since we don't serialize the title label in the designer, but it's worth checking in case it does.
   */
  _afterDeserialize() {
    if (this._components != null) {
      for (const comp of this._components) {
        if (comp instanceof Label && comp.id.startsWith(_autoTitleLabelPrefix))
          this._titleLabel = comp;
      }
    }
  }

  override getPropertyDefaultValue(prop: ComponentPropDefinition): any {
    if (prop.name === "padding" && this.tabStyle === TabStyle.SCROLL)
      return 16;
    if (prop.name === "paddingTop" && this.tabStyle === TabStyle.SCROLL)
      return 24;
    return super.getPropertyDefaultValue(prop);
  }

  override getListenerDefs(): Collection<ListenerListDef> {
    return {
      ...super.getListenerDefs(),
      "beforeSelection": { ..._beforeSelectionListenerDef },
      "afterSelection": { ..._afterSelectionListenerDef },
      "beforeClose": { ..._beforeCloseListenerDef },
      "afterClose": { ..._afterCloseListenerDef }
    };
  }

  override _syncVisible() {
    super._syncVisible();
    if (this.heading != null)
      this.heading.visible = this.visible;
    }

    private selectInDesigner() {
        if (this._designer != null)
            this._designer.selectComponent(this);
  }
}

ComponentTypes.registerComponentType("tab", Tab.prototype.constructor);
